import { ModuleType } from 'infrastructure/enums';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseInput from 'components/atoms/base-input';
import BaseForm from 'components/atoms/base-form';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useEffect, useMemo, type FC } from 'react';
import {
  isCcmModule,
  isCombinedModule,
  isRpmModule,
} from 'infrastructure/functions';
import classNames from 'classnames';
import { useModule } from 'infrastructure/providers/module-provider';
import { useFetchComments } from 'infrastructure/hooks/comments/use-fetch';
import MasterModal from 'components/organisms/masterModal';
import { Segmented } from 'antd';

import { createCaseInEhrSchema, type Values } from './validation-schema';
import s from './style.module.scss';

import type { CreateCaseInEhrDataProps } from 'infrastructure/hooks/control-panel/api';
import type { FormikHelpers } from 'formik';
import type { TCreateCaseInEhr } from 'infrastructure/hooks/control-panel/use-create-case-in-ehr';

interface ManualInteractionTimeModalNewProps {
  isOpen: boolean;
  toggle: () => void;
  patientGuid: string;
  module: ModuleType;
  loading: boolean;
  submit: TCreateCaseInEhr;
  tabsProps?: {
    showTabs: boolean;
    disableCcm: boolean;
    disableRpm: boolean;
  };
}

const CreateCaseInEhrModalNew: FC<ManualInteractionTimeModalNewProps> = ({
  isOpen,
  toggle,
  patientGuid,
  module,
  loading,
  submit,
  tabsProps,
}) => {
  const { t } = useTranslation();

  const { module: moduleType, changeModule: setModuleType } = useModule(module);

  const { refetch } = useFetchComments({
    patientGuid,
    enabled: false,
    meta: {
      commentType: moduleType,
    },
  });

  const activeKey = useMemo(() => {
    if (isRpmModule(moduleType) && tabsProps?.disableRpm) {
      return ModuleType.CCM;
    }
    if (isCcmModule(moduleType) && tabsProps?.disableCcm) {
      return ModuleType.RPM;
    }
    return moduleType;
  }, [moduleType, tabsProps]);

  const onSubmit = async (
    { subject, description }: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => {
    if (!subject || !description) return;

    const postData: CreateCaseInEhrDataProps = {
      isRpm: isRpmModule(moduleType),
      subject,
      description,
      patientGuid,
    };

    submit(postData, {
      onSuccess: () => {
        formikHelpers.resetForm();
        refetch();
      },
    });
  };

  const formik = useFormik<Values>({
    initialValues: createCaseInEhrSchema().cast({}),
    enableReinitialize: true,
    onSubmit,
    validateOnChange: false,
    validationSchema: createCaseInEhrSchema(),
  });

  const { submitForm, resetForm } = formik;

  const onCancel = () => {
    resetForm();
    toggle();
  };

  useEffect(() => {
    if (isRpmModule(module)) setModuleType(ModuleType.RPM);
    if (isCcmModule(module)) setModuleType(ModuleType.CCM);
  }, [module]);

  useEffect(() => {
    if (tabsProps?.disableRpm) setModuleType(ModuleType.CCM);
    if (tabsProps?.disableCcm) setModuleType(ModuleType.RPM);
  }, [tabsProps?.disableCcm, tabsProps?.disableRpm]);

  return (
    <>
      {isOpen && (
        <MasterModal
          title={t('labels.createCaseInEHR')}
          isOpen={isOpen}
          toggle={onCancel}
          size="medium"
          bodyClassName={s.body}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          footer={
            <>
              <BaseButton
                dataCy="cancel-button"
                key="cancel"
                type="secondary"
                label={t('controls.cancel')}
                onClick={onCancel}
              />
              <BaseButton
                dataCy="add-time-button"
                key="add-time-button"
                type="primary"
                label={t('controls.send')}
                loading={loading}
                onClick={submitForm}
              />
            </>
          }
        >
          <BaseForm formik={formik}>
            <div
              className={classNames(s['form-wrapper'], {
                [s['without-switcher']]: !isCombinedModule(module),
              })}
            >
              <BaseFormItem
                name="subject"
                label={t('labels.subject')}
                wrapperClassName={s.time}
                isBlackLabel
                required
              >
                <BaseInput />
              </BaseFormItem>
              {tabsProps?.showTabs && (
                <div className={s.switcher}>
                  <Segmented
                    options={[
                      {
                        label: 'RPM',
                        value: ModuleType.RPM,
                        disabled: tabsProps?.disableRpm,
                      },
                      {
                        label: 'CCM',
                        value: ModuleType.CCM,
                        disabled: tabsProps?.disableCcm,
                      },
                    ]}
                    value={activeKey}
                    onChange={(value) => setModuleType(value as ModuleType)}
                    className={s.segmented}
                  />
                </div>
              )}
              <BaseFormItem
                name="description"
                wrapperClassName={s.comment}
                label={t('labels.description')}
                isBlackLabel
                required
              >
                <BaseTextArea
                  rows={8}
                  placeholder={t('labels.typeHereYourCaseDescription')}
                />
              </BaseFormItem>
            </div>
          </BaseForm>
        </MasterModal>
      )}
    </>
  );
};
export default CreateCaseInEhrModalNew;
